import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CalendarDays, Check, CircleStop, LoaderCircle, Plus, RefreshCw,
  Save, Search, Send, Trash2, Trophy, X,
} from 'lucide-react'
import { api, getApiErrorMessage } from '../../lib/api'

const unwrap = (response) => response?.data?.data ?? response?.data ?? null
const pad = (value) => String(value).padStart(2, '0')

function localDateTime(value) {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return ''
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function formatDate(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date)
}

function formatNumber(value, suffix = '') {
  return `${new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 1 }).format(Number(value) || 0)}${suffix}`
}

function emptyForm() {
  const start = new Date()
  start.setSeconds(0, 0)
  const end = new Date(start)
  end.setDate(end.getDate() + 30)
  return {
    title: '', startsAt: localDateTime(start), endsAt: localDateTime(end),
    dailyAttemptLimit: 3, minimumFullDurationSeconds: 120,
    minimumQuickDurationSeconds: 15, safetyPassOnly: true, missions: [],
  }
}

function toForm(season) {
  if (!season) return emptyForm()
  return {
    title: season.title || '',
    startsAt: localDateTime(season.startsAt),
    endsAt: localDateTime(season.endsAt),
    dailyAttemptLimit: season.dailyAttemptLimit ?? 3,
    minimumFullDurationSeconds: season.minimumFullDurationSeconds ?? 120,
    minimumQuickDurationSeconds: season.minimumQuickDurationSeconds ?? 15,
    safetyPassOnly: season.safetyPassOnly !== false,
    missions: (season.missions || []).map((item) => ({
      contentType: item.contentType,
      contentCode: item.contentCode,
      scoreWeight: Number(item.scoreWeight) || 1,
    })),
  }
}

function toPayload(form) {
  return {
    title: form.title.trim(),
    startsAt: new Date(form.startsAt).toISOString(),
    endsAt: new Date(form.endsAt).toISOString(),
    dailyAttemptLimit: Number(form.dailyAttemptLimit),
    minimumFullDurationSeconds: Number(form.minimumFullDurationSeconds),
    minimumQuickDurationSeconds: Number(form.minimumQuickDurationSeconds),
    safetyPassOnly: form.safetyPassOnly,
    missions: form.missions.map((item) => ({ ...item, scoreWeight: Number(item.scoreWeight) })),
  }
}

const missionKey = (item) => `${item.contentType}:${item.contentCode}`

function Ranking({ data, selected }) {
  const rankings = Array.isArray(data?.rankings) ? data.rankings : []
  const anti = data?.antiGaming
  return (
    <section className="mt-8 border-t border-slate-200 pt-5" aria-labelledby="season-ranking-title">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 id="season-ranking-title" className="flex items-center gap-2 text-base font-semibold text-slate-950">
          <Trophy className="h-5 w-5 text-amber-600" aria-hidden="true" />
          {selected ? '선택한 시즌 순위' : '현재 시즌 순위'}
        </h3>
        {data?.season?.title && <span className="text-sm text-slate-600">{data.season.title}</span>}
      </div>
      <div className="mt-4 overflow-x-auto border-y border-slate-200">
        <table className="w-full min-w-[620px] text-left text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500">
            <tr><th className="px-4 py-3 text-center font-medium">순위</th><th className="px-4 py-3 font-medium">설계사</th><th className="px-4 py-3 text-right font-medium">점수</th><th className="px-4 py-3 text-right font-medium">평균</th><th className="px-4 py-3 text-right font-medium">인정 연습</th><th className="px-4 py-3 text-right font-medium">연속 참여</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rankings.map((item) => (
              <tr key={`${item.rank}-${item.plannerLabel}`} className={item.mine ? 'bg-sky-50' : ''}>
                <td className="px-4 py-3 text-center font-semibold">{item.rank}</td>
                <td className="px-4 py-3 font-medium">{item.plannerLabel}</td>
                <td className="px-4 py-3 text-right font-semibold text-sky-700">{formatNumber(item.points, '점')}</td>
                <td className="px-4 py-3 text-right">{formatNumber(item.averageScore, '점')}</td>
                <td className="px-4 py-3 text-right">{formatNumber(item.acceptedAttempts, '회')}</td>
                <td className="px-4 py-3 text-right">{formatNumber(item.streakDays, '일')}</td>
              </tr>
            ))}
            {rankings.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">아직 순위에 반영된 연습이 없습니다.</td></tr>}
          </tbody>
        </table>
      </div>
      {anti && (
        <dl className="mt-4 grid border-y border-slate-200 text-sm sm:grid-cols-2 xl:grid-cols-5">
          {[
            ['전체 시도', anti.totalAttempts], ['순위 반영', anti.acceptedAttempts],
            ['준법 제외', anti.excludedForSafety], ['시간 미달', anti.excludedForShortDuration],
            ['하루 제한 초과', anti.excludedByDailyLimit],
          ].map(([label, value]) => <div key={label} className="px-4 py-3"><dt className="text-xs text-slate-500">{label}</dt><dd className="mt-1 font-semibold">{formatNumber(value, '회')}</dd></div>)}
        </dl>
      )}
      {anti?.dataLimitReached && <p className="mt-2 text-xs font-medium text-amber-700">조회 한도를 넘어 일부 연습만 집계했습니다.</p>}
    </section>
  )
}

export default function CounselPracticeSeasonManager() {
  const [catalog, setCatalog] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [filter, setFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('ALL')
  const [leaderboard, setLeaderboard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const selected = useMemo(
    () => catalog?.seasons?.find((item) => item.id === selectedId) || null,
    [catalog, selectedId],
  )

  const load = useCallback(async (preferredId = undefined) => {
    setLoading(true)
    setError('')
    try {
      const response = await api.get('/counsel-practice/admin/seasons', { __silent: true })
      const loaded = unwrap(response) || {}
      const seasons = Array.isArray(loaded.seasons) ? loaded.seasons : []
      setCatalog({ ...loaded, seasons, contentOptions: loaded.contentOptions || [] })
      setLeaderboard(loaded.activeLeaderboard || null)
      setSelectedId((current) => {
        const candidate = preferredId === undefined ? current : preferredId
        return seasons.some((item) => item.id === candidate) ? candidate : null
      })
    } catch (e) {
      setError(getApiErrorMessage(e, '시즌 정보를 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(null) }, [load])

  useEffect(() => {
    if (!selected) return undefined
    setForm(toForm(selected))
    let mounted = true
    api.get(`/counsel-practice/admin/seasons/${selected.id}/leaderboard`, { __silent: true })
      .then((response) => { if (mounted) setLeaderboard(unwrap(response)) })
      .catch((e) => { if (mounted) setError(getApiErrorMessage(e, '시즌 순위를 불러오지 못했습니다.')) })
    return () => { mounted = false }
  }, [selected])

  const options = useMemo(() => {
    const query = filter.trim().toLocaleLowerCase('ko-KR')
    return (catalog?.contentOptions || []).filter((item) => (
      (typeFilter === 'ALL' || item.contentType === typeFilter)
      && (!query || `${item.title} ${item.insuranceTypeLabel}`.toLocaleLowerCase('ko-KR').includes(query))
    ))
  }, [catalog, filter, typeFilter])

  const missionMap = useMemo(
    () => new Map(form.missions.map((item) => [missionKey(item), item])),
    [form.missions],
  )

  const newSeason = () => {
    setSelectedId(null)
    setForm(emptyForm())
    setLeaderboard(catalog?.activeLeaderboard || null)
    setError('')
    setNotice('')
  }

  const chooseSeason = (season) => {
    setSelectedId(season.id)
    setForm(toForm(season))
    setError('')
    setNotice('')
  }

  const toggleMission = (option) => {
    if (selected && !selected.editable) return
    setForm((current) => {
      const key = missionKey(option)
      const found = current.missions.some((item) => missionKey(item) === key)
      return {
        ...current,
        missions: found
          ? current.missions.filter((item) => missionKey(item) !== key)
          : [...current.missions, { contentType: option.contentType, contentCode: option.contentCode, scoreWeight: 1 }],
      }
    })
  }

  const save = async () => {
    if (!form.title.trim()) return setError('시즌 이름을 입력해 주세요.')
    if (!form.startsAt || !form.endsAt) return setError('시즌 시작과 종료 시각을 입력해 주세요.')
    if (form.missions.length === 0) return setError('시즌에 포함할 연습을 하나 이상 선택해 주세요.')
    setSaving(true)
    setError('')
    setNotice('')
    try {
      const response = selectedId
        ? await api.put(`/counsel-practice/admin/seasons/${selectedId}`, toPayload(form), { __silent: true })
        : await api.post('/counsel-practice/admin/seasons', toPayload(form), { __silent: true })
      const saved = unwrap(response)
      setNotice(selectedId ? '시즌 설정을 저장했습니다.' : '새 시즌을 만들었습니다.')
      await load(saved?.id || selectedId)
    } catch (e) {
      setError(getApiErrorMessage(e, '시즌 설정을 저장하지 못했습니다.'))
    } finally {
      setSaving(false)
    }
  }

  const changeStatus = async (action) => {
    const copy = {
      publish: ['시즌을 시작할까요?', '시즌을 시작했습니다.'],
      end: ['시즌을 종료할까요?', '시즌을 종료했습니다.'],
      delete: ['준비 중인 시즌을 삭제할까요?', '시즌을 삭제했습니다.'],
    }[action]
    if (!selectedId || !window.confirm(copy[0])) return
    setSaving(true)
    setError('')
    try {
      if (action === 'delete') {
        await api.delete(`/counsel-practice/admin/seasons/${selectedId}`, { __silent: true })
        setForm(emptyForm())
        await load(null)
      } else {
        await api.post(`/counsel-practice/admin/seasons/${selectedId}/${action}`, null, { __silent: true })
        await load(selectedId)
      }
      setNotice(copy[1])
    } catch (e) {
      setError(getApiErrorMessage(e, '시즌 상태를 변경하지 못했습니다.'))
    } finally {
      setSaving(false)
    }
  }

  if (loading && !catalog) {
    return <div className="flex min-h-[28rem] items-center justify-center text-slate-500"><LoaderCircle className="h-6 w-6 animate-spin" aria-label="시즌 정보 불러오는 중" /></div>
  }

  return (
    <section className="mt-5" data-testid="counsel-season-manager" aria-labelledby="counsel-season-title">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
        <div>
          <h2 id="counsel-season-title" className="flex items-center gap-2 text-lg font-semibold text-slate-950">
            <CalendarDays className="h-5 w-5 text-sky-700" aria-hidden="true" />시즌·랭킹
          </h2>
          <p className="mt-1 text-sm text-slate-600">기간과 인정 기준을 정해 꾸준한 연습 결과를 순위로 보여줍니다.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={newSeason} disabled={saving} className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"><Plus className="h-4 w-4" aria-hidden="true" />새 시즌</button>
          <button type="button" onClick={() => load()} disabled={loading || saving} className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50" title="새로고침" aria-label="시즌 정보 새로고침"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" /></button>
        </div>
      </div>

      {error && <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">{error}</div>}
      {notice && <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" role="status">{notice}</div>}

      <div className="mt-6 grid gap-6 xl:grid-cols-[18rem_minmax(0,1fr)]">
        <aside className="border-r border-slate-200 pr-0 xl:pr-5" aria-label="시즌 목록">
          <h3 className="pb-3 font-semibold text-slate-950">시즌 목록</h3>
          <div className="border-y border-slate-200">
            {(catalog?.seasons || []).map((season) => (
              <button key={season.id} type="button" onClick={() => chooseSeason(season)} aria-pressed={season.id === selectedId} className={`block w-full border-b border-slate-100 px-3 py-3 text-left last:border-b-0 ${season.id === selectedId ? 'bg-sky-50' : 'hover:bg-slate-50'}`}>
                <div className="flex items-start justify-between gap-3">
                  <span className="line-clamp-2 text-sm font-semibold text-slate-900">{season.title}</span>
                  <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${season.statusLabel === '진행 중' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>{season.statusLabel}</span>
                </div>
                <div className="mt-2 text-xs leading-5 text-slate-500">{formatDate(season.startsAt)}<br />~ {formatDate(season.endsAt)}</div>
              </button>
            ))}
            {(catalog?.seasons || []).length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-500">만든 시즌이 없습니다.</p>}
          </div>
        </aside>

        <div className="min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 pb-4">
            <div><h3 className="text-base font-semibold text-slate-950">{selected?.title || '새 시즌'}</h3>{selected && <p className="mt-1 text-xs text-slate-500">{selected.statusLabel} · 연습 {selected.missions?.length || 0}개</p>}</div>
            <div className="flex flex-wrap gap-2">
              {selected?.publishable && <button type="button" onClick={() => changeStatus('publish')} disabled={saving} className="inline-flex h-9 items-center gap-2 rounded-md bg-emerald-700 px-3 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50"><Send className="h-4 w-4" aria-hidden="true" />시즌 시작</button>}
              {selected?.endable && <button type="button" onClick={() => changeStatus('end')} disabled={saving} className="inline-flex h-9 items-center gap-2 rounded-md border border-amber-300 px-3 text-sm font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-50"><CircleStop className="h-4 w-4" aria-hidden="true" />시즌 종료</button>}
              {selected?.editable && <button type="button" onClick={() => changeStatus('delete')} disabled={saving} className="flex h-9 w-9 items-center justify-center rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50" title="시즌 삭제" aria-label="시즌 삭제"><Trash2 className="h-4 w-4" aria-hidden="true" /></button>}
            </div>
          </div>

          <fieldset disabled={Boolean(selected && !selected.editable) || saving} className="mt-5 min-w-0 w-full disabled:opacity-75">
            <div className="grid gap-4 lg:grid-cols-2">
              <label className="block lg:col-span-2"><span className="text-sm font-medium text-slate-700">시즌 이름</span><input type="text" maxLength={120} value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} className="mt-2 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" placeholder="예: 실손보험 상담 집중 연습" /></label>
              <label className="block"><span className="text-sm font-medium text-slate-700">시작</span><input type="datetime-local" value={form.startsAt} onChange={(event) => setForm((current) => ({ ...current, startsAt: event.target.value }))} className="mt-2 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" /></label>
              <label className="block"><span className="text-sm font-medium text-slate-700">종료</span><input type="datetime-local" value={form.endsAt} onChange={(event) => setForm((current) => ({ ...current, endsAt: event.target.value }))} className="mt-2 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" /></label>
            </div>

            <div className="mt-5 grid overflow-hidden border border-slate-200 sm:grid-cols-2 xl:grid-cols-4">
              <label className="px-4 py-3"><span className="block text-xs font-medium text-slate-500">하루 인정 횟수</span><input type="number" min="1" max="10" value={form.dailyAttemptLimit} onChange={(event) => setForm((current) => ({ ...current, dailyAttemptLimit: event.target.value }))} className="mt-2 h-9 w-full rounded-md border border-slate-300 px-3 text-sm" /></label>
              <label className="px-4 py-3"><span className="block text-xs font-medium text-slate-500">5분 상담 최소 시간(초)</span><input type="number" min="30" max="600" value={form.minimumFullDurationSeconds} onChange={(event) => setForm((current) => ({ ...current, minimumFullDurationSeconds: event.target.value }))} className="mt-2 h-9 w-full rounded-md border border-slate-300 px-3 text-sm" /></label>
              <label className="px-4 py-3"><span className="block text-xs font-medium text-slate-500">1분 미션 최소 시간(초)</span><input type="number" min="5" max="120" value={form.minimumQuickDurationSeconds} onChange={(event) => setForm((current) => ({ ...current, minimumQuickDurationSeconds: event.target.value }))} className="mt-2 h-9 w-full rounded-md border border-slate-300 px-3 text-sm" /></label>
              <label className="flex items-center gap-3 px-4 py-3"><input type="checkbox" checked={form.safetyPassOnly} onChange={(event) => setForm((current) => ({ ...current, safetyPassOnly: event.target.checked }))} className="h-4 w-4 rounded border-slate-300 text-sky-700" /><span className="text-sm font-medium text-slate-700">준법 통과만 인정</span></label>
            </div>

            <section className="mt-6" aria-labelledby="season-missions-title">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div><h4 id="season-missions-title" className="text-sm font-semibold text-slate-900">순위에 반영할 연습</h4><p className="mt-1 text-xs text-slate-500">선택 {form.missions.length}개</p></div>
                <div className="flex flex-wrap gap-2">
                  <div className="relative"><Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" aria-hidden="true" /><input type="search" value={filter} onChange={(event) => setFilter(event.target.value)} className="h-9 w-56 rounded-md border border-slate-300 pl-9 pr-3 text-sm" placeholder="보험 종류나 연습 검색" aria-label="시즌 연습 검색" /></div>
                  <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm" aria-label="연습 유형"><option value="ALL">전체 유형</option><option value="FULL_SCENARIO">5분 상담</option><option value="QUICK_MISSION">1분 미션</option></select>
                </div>
              </div>
              <div className="mt-3 max-h-80 overflow-auto border-y border-slate-200">
                <table className="w-full min-w-[620px] text-left text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-xs text-slate-500"><tr><th className="w-12 px-4 py-3 font-medium">선택</th><th className="px-4 py-3 font-medium">연습</th><th className="px-4 py-3 font-medium">보험 종류</th><th className="px-4 py-3 font-medium">유형</th><th className="w-32 px-4 py-3 text-right font-medium">점수 배수</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {options.map((option) => {
                      const chosen = missionMap.get(missionKey(option))
                      return (
                        <tr key={missionKey(option)} className={chosen ? 'bg-sky-50' : ''}>
                          <td className="px-4 py-3"><button type="button" onClick={() => toggleMission(option)} disabled={Boolean(selected && !selected.editable)} className={`flex h-6 w-6 items-center justify-center rounded border ${chosen ? 'border-sky-700 bg-sky-700 text-white' : 'border-slate-300 bg-white text-transparent'}`} aria-label={`${option.title} ${chosen ? '선택 해제' : '선택'}`} aria-pressed={Boolean(chosen)}><Check className="h-4 w-4" aria-hidden="true" /></button></td>
                          <td className="px-4 py-3 font-medium text-slate-900">{option.title}</td><td className="px-4 py-3 text-slate-600">{option.insuranceTypeLabel}</td><td className="px-4 py-3 text-slate-600">{option.contentTypeLabel}</td>
                          <td className="px-4 py-2 text-right">{chosen ? <label className="inline-flex items-center gap-1"><input type="number" min="0.5" max="2" step="0.1" value={chosen.scoreWeight} onChange={(event) => setForm((current) => ({ ...current, missions: current.missions.map((item) => missionKey(item) === missionKey(option) ? { ...item, scoreWeight: event.target.value } : item) }))} className="h-9 w-20 rounded-md border border-slate-300 px-2 text-right text-sm" aria-label={`${option.title} 점수 배수`} /><span className="text-xs text-slate-500">배</span></label> : <span className="text-slate-400">-</span>}</td>
                        </tr>
                      )
                    })}
                    {options.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">조건에 맞는 연습이 없습니다.</td></tr>}
                  </tbody>
                </table>
              </div>
            </section>
          </fieldset>

          {(!selected || selected.editable) && (
            <div className="mt-5 flex justify-end gap-2">
              {selected && <button type="button" onClick={() => setForm(toForm(selected))} disabled={saving} className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"><X className="h-4 w-4" aria-hidden="true" />되돌리기</button>}
              <button type="button" onClick={save} disabled={saving} className="inline-flex h-10 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">{saving ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Save className="h-4 w-4" aria-hidden="true" />}{selected ? '변경 저장' : '시즌 만들기'}</button>
            </div>
          )}
        </div>
      </div>

      <Ranking data={leaderboard} selected={Boolean(selected)} />
    </section>
  )
}
